/**
 * EmptyState - Empty state display for notifications
 * Shows friendly message when no notifications are available
 */

import type { ElementType } from "react";
import { SvgIcon } from "@mui/material";
import { IntlProvider, useIntl } from "react-intl";
import { BellBadge } from "@opengov/react-capital-assets";
import { Result } from "@opengov/components-result";
import { styles } from "./EmptyState.styles";
import { DEFAULT_LOCALE } from "../NavBar/utils/intl";
import messages from "./locales/en-US.json";

interface EmptyStateProps {
  /** Title text */
  readonly title?: string;
  /** Subtitle text */
  readonly subtitle?: string;
}

/**
 * EmptyState component
 *
 * Displays friendly empty state with bell icon when:
 * - No notifications exist
 * - Search returns no results
 * - No unread notifications
 *
 * Uses the Capital Design System Result component for consistent empty states.
 *
 * @example
 * ```tsx
 * {notifications.length === 0 && (
 *   <EmptyState
 *     title="No notifications"
 *     subtitle="You're all caught up!"
 *   />
 * )}
 * ```
 */
function EmptyStateView({ title, subtitle }: EmptyStateProps) {
  const intl = useIntl();

  const resolvedTitle = title ?? intl.formatMessage({ id: "emptyState.title", defaultMessage: "No notifications" });
  const resolvedSubtitle =
    subtitle ?? intl.formatMessage({ id: "emptyState.subtitle", defaultMessage: "You're all caught up!" });
  const bellIcon = <SvgIcon component={BellBadge as ElementType} inheritViewBox />;

  return (
    <Result
      icon={bellIcon}
      title={resolvedTitle}
      subTitle={resolvedSubtitle}
      size="large"
      status="empty"
      sx={styles.container}
    />
  );
}

// Scoped provider: rendered inside the notification drawer, whose catalog doesn't include these messages.
export function EmptyState(props: EmptyStateProps) {
  return (
    <IntlProvider locale={DEFAULT_LOCALE} messages={messages}>
      <EmptyStateView {...props} />
    </IntlProvider>
  );
}
